<template>
    <DualloLoaderWrapper :loading="isLoading" message="Loading scan status...">
        <div class="pipeline">
            <!-- Header: scan identity + overall progress -->
            <header class="pipeline-head">
                <div class="head-row">
                    <h1 class="scan-title">{{ scan.name }}</h1>
                    <ScanStatusPill :status="scan.status" />
                </div>
                <div v-if="fileStatus" class="progress">
                    <div class="progress-track">
                        <div class="progress-fill" :style="{ width: progressPct + '%' }"></div>
                    </div>
                    <span class="progress-label">{{ completedSteps }} of {{ totalSteps }} stages ready</span>
                </div>
            </header>

            <!-- Step 1 — Source file (download only) -->
            <section class="step" :class="{ 'is-done': sourceDone }">
                <div class="step-badge">
                    <v-icon size="20">{{ sourceDone ? 'mdi-check' : 'mdi-numeric-1' }}</v-icon>
                </div>
                <div class="step-body">
                    <div class="step-head">
                        <div class="step-heading">
                            <h3 class="step-title">Source file</h3>
                            <p class="step-sub">The original capture (.mp4 / .insv).</p>
                        </div>
                        <span class="status-chip" :class="sourceDone ? 'is-ok' : 'is-missing'">
                            <v-icon size="14">{{ sourceDone ? 'mdi-check-circle' : 'mdi-alert-circle-outline' }}</v-icon>
                            {{ sourceDone ? 'Present' : 'Missing' }}
                        </span>
                    </div>
                    <div class="step-actions">
                        <v-btn variant="outlined" prepend-icon="mdi-download" :disabled="!sourceDone"
                            @click="downloadSourceFile">Download source</v-btn>
                    </div>
                </div>
            </section>

            <!-- Step 2 — Processed video -->
            <section class="step" :class="{ 'is-done': videoDone }">
                <div class="step-badge">
                    <v-icon size="20">{{ videoDone ? 'mdi-check' : 'mdi-numeric-2' }}</v-icon>
                </div>
                <div class="step-body">
                    <div class="step-head">
                        <div class="step-heading">
                            <h3 class="step-title">Processed video</h3>
                            <p class="step-sub">The stabilised / processed .mp4 used for frame extraction.</p>
                        </div>
                        <span class="status-chip" :class="videoDone ? 'is-ok' : 'is-pending'">
                            <v-icon size="14">{{ videoDone ? 'mdi-check-circle' : 'mdi-upload-outline' }}</v-icon>
                            {{ videoDone ? 'Uploaded' : 'Awaiting upload' }}
                        </span>
                    </div>
                    <div class="upload-item">
                        <FileUploadButton label="Choose video (.mp4)" accept="video/mp4" @file-selected="onVideoSelected" />
                        <v-btn class="upload-go" :disabled="!files.video" :loading="uploading.video" variant="tonal"
                            prepend-icon="mdi-cloud-upload-outline" @click="uploadVideo">Upload</v-btn>
                    </div>
                </div>
            </section>

            <!-- Step 3 — Model data -->
            <section class="step" :class="{ 'is-done': modelDone }">
                <div class="step-badge">
                    <v-icon size="20">{{ modelDone ? 'mdi-check' : 'mdi-numeric-3' }}</v-icon>
                </div>
                <div class="step-body">
                    <div class="step-head">
                        <div class="step-heading">
                            <h3 class="step-title">Model data</h3>
                            <p class="step-sub">Point cloud, camera pose files and the extracted frames.</p>
                        </div>
                        <span class="status-chip" :class="modelDone ? 'is-ok' : 'is-pending'">
                            <v-icon size="14">{{ modelDone ? 'mdi-check-circle' : 'mdi-dots-horizontal' }}</v-icon>
                            {{ modelDone ? 'Complete' : 'Incomplete' }}
                        </span>
                    </div>

                    <div class="upload-list">
                        <div class="upload-item" :class="{ 'is-done': fileStatus && fileStatus.hasModelFile }">
                            <div class="upload-info">
                                <v-icon size="18" class="tick">{{ fileStatus && fileStatus.hasModelFile ? 'mdi-check-circle' : 'mdi-circle-outline' }}</v-icon>
                                <div class="upload-text">
                                    <span class="upload-name">Point cloud (.ply)</span>
                                    <span class="upload-meta">{{ fileStatus && fileStatus.hasModelFile ? 'Uploaded' : 'Not uploaded' }}</span>
                                </div>
                            </div>
                            <div class="upload-controls">
                                <FileUploadButton label="Choose" @file-selected="files.ply = $event" />
                                <v-btn class="upload-go" :disabled="!files.ply" :loading="uploading.ply" variant="tonal"
                                    prepend-icon="mdi-cloud-upload-outline" @click="uploadPly">Upload</v-btn>
                            </div>
                        </div>

                        <div class="upload-item" :class="{ 'is-done': fileStatus && fileStatus.hasExternalsFile }">
                            <div class="upload-info">
                                <v-icon size="18" class="tick">{{ fileStatus && fileStatus.hasExternalsFile ? 'mdi-check-circle' : 'mdi-circle-outline' }}</v-icon>
                                <div class="upload-text">
                                    <span class="upload-name">Externals.txt</span>
                                    <span class="upload-meta">{{ fileStatus && fileStatus.hasExternalsFile ? 'Uploaded' : 'Not uploaded' }}</span>
                                </div>
                            </div>
                            <div class="upload-controls">
                                <FileUploadButton label="Choose" accept=".txt" @file-selected="files.externals = $event" />
                                <v-btn class="upload-go" :disabled="!files.externals" :loading="uploading.externals" variant="tonal"
                                    prepend-icon="mdi-cloud-upload-outline" @click="uploadExternals">Upload</v-btn>
                            </div>
                        </div>

                        <div class="upload-item" :class="{ 'is-done': fileStatus && fileStatus.hasInternalsFile }">
                            <div class="upload-info">
                                <v-icon size="18" class="tick">{{ fileStatus && fileStatus.hasInternalsFile ? 'mdi-check-circle' : 'mdi-circle-outline' }}</v-icon>
                                <div class="upload-text">
                                    <span class="upload-name">Internals.txt</span>
                                    <span class="upload-meta">{{ fileStatus && fileStatus.hasInternalsFile ? 'Uploaded' : 'Not uploaded' }}</span>
                                </div>
                            </div>
                            <div class="upload-controls">
                                <FileUploadButton label="Choose" accept=".txt" @file-selected="files.internals = $event" />
                                <v-btn class="upload-go" :disabled="!files.internals" :loading="uploading.internals" variant="tonal"
                                    prepend-icon="mdi-cloud-upload-outline" @click="uploadInternals">Upload</v-btn>
                            </div>
                        </div>

                        <div class="upload-item" :class="{ 'is-done': fileStatus && fileStatus.hasSourceFrames }">
                            <div class="upload-info">
                                <v-icon size="18" class="tick">{{ fileStatus && fileStatus.hasSourceFrames ? 'mdi-check-circle' : 'mdi-circle-outline' }}</v-icon>
                                <div class="upload-text">
                                    <span class="upload-name">Frames</span>
                                    <span class="upload-meta">
                                        {{ fileStatus && fileStatus.hasSourceFrames
                                            ? `${fileStatus.numberOfSourceFrames} uploaded` : 'Not uploaded' }}
                                    </span>
                                </div>
                            </div>
                            <div class="upload-controls">
                                <FileUploadButton label="Choose" accept="image/*" multiple @file-selected="files.frames = $event" />
                                <v-btn class="upload-go" :disabled="!files.frames" :loading="uploading.frames" variant="tonal"
                                    prepend-icon="mdi-cloud-upload-outline" @click="uploadFrames">Upload</v-btn>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <!-- Step 4 — Generate records & finalise -->
            <section v-if="fileStatus" class="step" :class="{ 'is-done': isComplete }">
                <div class="step-badge">
                    <v-icon size="20">{{ isComplete ? 'mdi-check' : 'mdi-numeric-4' }}</v-icon>
                </div>
                <div class="step-body">
                    <div class="step-head">
                        <div class="step-heading">
                            <h3 class="step-title">Generate records &amp; finish</h3>
                            <p class="step-sub">Process the uploaded files into frame &amp; camera records, then mark the scan complete.</p>
                        </div>
                        <span class="status-chip" :class="isComplete ? 'is-ok' : 'is-pending'">
                            <v-icon size="14">{{ isComplete ? 'mdi-check-circle' : 'mdi-cog-outline' }}</v-icon>
                            {{ isComplete ? 'Complete' : 'Pending' }}
                        </span>
                    </div>

                    <!-- Human-readable readiness checklist -->
                    <div class="checklist">
                        <div v-for="c in recordChecks" :key="c.label" class="check" :class="{ ok: c.ok }">
                            <v-icon size="16">{{ c.ok ? 'mdi-check-circle' : 'mdi-circle-outline' }}</v-icon>
                            <span class="check-label">{{ c.label }}</span>
                            <span v-if="c.ok" class="check-count">{{ c.count }}</span>
                        </div>
                    </div>

                    <!-- Sequential processing actions -->
                    <div class="process-actions">
                        <v-btn color="primary" variant="flat" prepend-icon="mdi-image-multiple-outline"
                            :loading="isProcessing" @click="processFrames"
                            :disabled="fileStatus.hasFrameRecords && fileStatus.hasWebpFrameThumbnails">
                            Process frames
                        </v-btn>
                        <v-btn color="primary" variant="flat" prepend-icon="mdi-camera-outline"
                            :loading="isProcessing" @click="processCameraHelpers"
                            :disabled="fileStatus.hasCameraHelperRecords">
                            Process camera helpers
                        </v-btn>
                        <v-btn color="success" variant="flat" prepend-icon="mdi-flag-checkered"
                            :loading="isProcessing" @click="updateScanComplete"
                            :disabled="!fileStatus.hasCameraHelperRecords || !fileStatus.hasFrameRecords || isComplete">
                            Mark scan complete
                        </v-btn>
                    </div>
                </div>
            </section>

            <!-- Danger zone -->
            <section class="danger-zone">
                <div class="danger-head">
                    <v-icon size="18" color="#ef5350">mdi-alert-outline</v-icon>
                    <h3 class="danger-title">Danger zone</h3>
                </div>
                <p class="danger-hint">
                    Force this scan into a failed state — use when a pipeline stage failed and the record needs to
                    reflect it. This is the same terminal state a real error would produce.
                </p>
                <v-btn color="error" variant="outlined" prepend-icon="mdi-close-octagon-outline"
                    @click="openCancelDialog">
                    Cancel scan / mark as failed
                </v-btn>
            </section>

            <v-dialog v-model="showCancelDialog" max-width="480">
                <v-card class="cancel-card">
                    <v-card-title class="cancel-title">Cancel scan</v-card-title>
                    <v-card-text>
                        <p class="cancel-text">
                            Mark this scan as failed. This updates the record just like a real pipeline error;
                            re-run the pipeline to recover.
                        </p>
                        <v-select v-model="cancelStage" :items="cancelStages" item-title="title" item-value="value"
                            label="Failure stage" variant="outlined" density="comfortable" hide-details="auto"
                            class="mb-4" />
                        <v-textarea v-model="cancelReason" label="Reason (optional)" variant="outlined"
                            density="comfortable" rows="3" auto-grow hide-details="auto"
                            placeholder="What went wrong?" />
                    </v-card-text>
                    <v-card-actions class="cancel-actions">
                        <v-btn variant="text" :disabled="cancelling" @click="showCancelDialog = false">Back</v-btn>
                        <v-btn color="error" variant="flat" :loading="cancelling" :disabled="!cancelStage"
                            @click="confirmCancel">
                            Mark as failed
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>
        </div>
    </DualloLoaderWrapper>
</template>

<script setup>
import { defineOptions, defineProps, defineEmits, computed, onMounted, ref, watch } from 'vue';
import organizationSasService from '@/services/OrganizationSasService';
import DualloAdminService from '@/services/DualloAdminService';
import { uploadToAzureBlobStorage } from '@/utils/blobStorageUtil';
import FileUploadButton from './FileUploadButton.vue';
import DualloLoaderWrapper from './DualloLoaderWrapper.vue';
import ScanStatusPill from './pills/ScanStatusPill.vue';

defineOptions({ name: 'ScanPipelineManager' });

const props = defineProps({
    scan: { type: Object, required: true },
    selectedOrg: { type: String, required: true }
});

const emit = defineEmits(['onScanUpdated'])

const isProcessing = ref(false);
const fileStatus = ref(null);

const isLoading = ref(true);

const files = ref({
    video: null,
    frames: null,
    thumbnails: null,
    ply: null,
    externals: null,
    internals: null
});

const uploading = ref({
    video: false,
    frames: false,
    thumbnails: false,
    ply: false,
    externals: false,
    internals: false
});

// ----- Derived stage state (drives the numbered steps + progress bar) -----
const sourceDone = computed(() => !!fileStatus.value?.hasSourceFile);
const videoDone = computed(() => !!fileStatus.value?.hasProcessedFile);
const modelDone = computed(() => {
    const s = fileStatus.value;
    return !!(s && s.hasModelFile && s.hasExternalsFile && s.hasInternalsFile && s.hasSourceFrames);
});
const isComplete = computed(() => props.scan.status === 'scan-processing-success');

const totalSteps = 4;
const completedSteps = computed(() =>
    [sourceDone.value, videoDone.value, modelDone.value, isComplete.value].filter(Boolean).length);
const progressPct = computed(() => Math.round((completedSteps.value / totalSteps) * 100));

// Human-readable readiness checklist (replaces the raw HasX / numberOfX flags).
const recordChecks = computed(() => {
    const s = fileStatus.value ?? {};
    return [
        { label: 'Source frames', ok: !!s.hasSourceFrames, count: s.numberOfSourceFrames },
        { label: 'Converted frames', ok: !!s.hasWebpFrames, count: s.numberOfWebPFrames },
        { label: 'Thumbnails', ok: !!s.hasWebpFrameThumbnails, count: s.numberOfWebPFrameThumbnails },
        { label: 'Frame records', ok: !!s.hasFrameRecords, count: s.numberOfFrameRecords },
        { label: 'Camera records', ok: !!s.hasCameraHelperRecords, count: s.numberOfCameraHelperRecords },
    ];
});

onMounted(async () => {
    try {
        await checkRecordsExistence();
    } catch (error) {
        console.error(error);
    } finally {
        isLoading.value = false;
    }
});

watch(() => props.scan, async () => {
    await checkRecordsExistence();
}, { deep: true });

const downloadSourceFile = async () => {
    try {
        const unsignedPath = await DualloAdminService.getScanSourceFilePath(props.selectedOrg, props.scan.assetId, props.scan.id);
        const signedPath = await organizationSasService.getSignedUrl(unsignedPath);
        const a = document.createElement('a');
        a.href = signedPath;
        a.target = '_blank';
        a.download = props.scan.name;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    } catch (error) {
        console.error(error);
    } finally {
        await triggerUpdateEmit();
    }
};

const uploadVideo = async () => {
    uploading.value.video = true;
    try {
        const sasResult = await DualloAdminService.getProcessedFileUploadSaSLink(
            props.selectedOrg,
            props.scan.assetId,
            props.scan.id,
            videoDuration.value
        );
        await uploadToAzureBlobStorage(sasResult.uploadUrl, files.value.video, files.value.video.type);
    } catch (error) {
        console.error(error);
    } finally {
        uploading.value.video = false;
        files.value.video = null;
        videoDuration.value = null;
        await triggerUpdateEmit();
    }
};

const triggerUpdateEmit = async () => {
    await new Promise(resolve => setTimeout(resolve, 500));
    emit('onScanUpdated');
}

const uploadFrames = async () => {
    uploading.value.frames = true;
    try {
        const fileNames = files.value.frames.map(f => f.name);
        const sasResult = await DualloAdminService.getFramesFileUploadSaSLink(
            props.selectedOrg,
            props.scan.assetId,
            props.scan.id,
            fileNames
        );
        await Promise.all(
            sasResult.map((result, i) =>
                uploadToAzureBlobStorage(result.uploadUrl, files.value.frames[i], "image/png")
            )
        );
    } catch (error) {
        console.error(error);
    } finally {
        uploading.value.frames = false;
        await triggerUpdateEmit();
    }
};

const videoDuration = ref(null);

const getVideoDuration = (file) => new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => { URL.revokeObjectURL(video.src); resolve(video.duration); };
    video.onerror = reject;
    video.src = URL.createObjectURL(file);
});

const onVideoSelected = async (file) => {
    files.value.video = file;
    videoDuration.value = await getVideoDuration(file);
};

const uploadPly = async () => {
    uploading.value.ply = true;
    try {
        const sasResult = await DualloAdminService.getModelFileUploadSaSLink(
            props.selectedOrg,
            props.scan.assetId,
            props.scan.id
        );
        await uploadToAzureBlobStorage(sasResult.uploadUrl, files.value.ply, "application/octet-stream");
    } catch (error) {
        console.error(error);
    } finally {
        uploading.value.ply = false;
        files.value.ply = null;
        await triggerUpdateEmit();
    }
};

const uploadExternals = async () => {
    uploading.value.externals = true;
    try {
        const sasResult = await DualloAdminService.getExternalsFileUploadSaSLink(
            props.selectedOrg,
            props.scan.assetId,
            props.scan.id
        );
        await uploadToAzureBlobStorage(sasResult.uploadUrl, files.value.externals, "text/plain");
    } catch (error) {
        console.error(error);
    } finally {
        uploading.value.externals = false;
        await triggerUpdateEmit();
    }
};

const uploadInternals = async () => {
    uploading.value.internals = true;
    try {
        const sasResult = await DualloAdminService.getInternalsFileUploadSaSLink(
            props.selectedOrg,
            props.scan.assetId,
            props.scan.id
        );
        await uploadToAzureBlobStorage(sasResult.uploadUrl, files.value.internals, "text/plain");
    } catch (error) {
        console.error(error);
    } finally {
        uploading.value.internals = false;
        await triggerUpdateEmit();
    }
};

const processFrames = async () => {
    isProcessing.value = true;
    try {
        await DualloAdminService.processFramesFiles(
            props.selectedOrg,
            props.scan.assetId,
            props.scan.id);
    } finally {
        isProcessing.value = false;
        await triggerUpdateEmit();
    }
};

const processCameraHelpers = async () => {
    isProcessing.value = true;
    try {
        await DualloAdminService.processCameraHelpers(
            props.selectedOrg,
            props.scan.assetId,
            props.scan.id);
    } finally {
        isProcessing.value = false;
        await triggerUpdateEmit();
    }
};

const updateScanComplete = async () => {
    isProcessing.value = true;
    try {
        await DualloAdminService.setScanProcessingAsComplete(
            props.selectedOrg,
            props.scan.assetId,
            props.scan.id);
    } finally {
        isProcessing.value = false;
        await triggerUpdateEmit();
    }
}

// Cancel / mark-as-failed. Stage values match the backend FailureStages constants.
const cancelStages = [
    { value: 'model-generation', title: 'Model Generation Failed' },
    { value: 'ai-analysis', title: 'AI Failed' },
    { value: 'scan-processing', title: 'Scan Failed' },
];
const showCancelDialog = ref(false);
const cancelStage = ref(null);
const cancelReason = ref('');
const cancelling = ref(false);

const openCancelDialog = () => {
    cancelStage.value = null;
    cancelReason.value = '';
    showCancelDialog.value = true;
};

const confirmCancel = async () => {
    if (!cancelStage.value) return;
    cancelling.value = true;
    try {
        await DualloAdminService.cancelScan(
            props.selectedOrg,
            props.scan.assetId,
            props.scan.id,
            cancelStage.value,
            cancelReason.value?.trim() || null);
        showCancelDialog.value = false;
    } catch (error) {
        console.error(error);
    } finally {
        cancelling.value = false;
        await triggerUpdateEmit();
    }
}

const checkRecordsExistence = async () => {
    fileStatus.value = await DualloAdminService.getFileStatus(props.selectedOrg,
        props.scan.assetId,
        props.scan.id);
}
</script>

<style scoped>
.pipeline {
    display: flex;
    flex-direction: column;
    gap: 14px;
    width: 100%;
}

/* ----- Header ----- */
.pipeline-head {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding-bottom: 4px;
}

.head-row {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
}

.scan-title {
    font-size: 1.4rem;
    font-weight: 700;
    color: #fff;
    margin: 0;
}

.progress {
    display: flex;
    align-items: center;
    gap: 12px;
}

.progress-track {
    flex: 1 1 auto;
    height: 6px;
    border-radius: 3px;
    background: rgba(255, 255, 255, 0.08);
    overflow: hidden;
}

.progress-fill {
    height: 100%;
    border-radius: 3px;
    background: linear-gradient(90deg, #64ffda, #39c9a6);
    transition: width 0.35s ease;
}

.progress-label {
    flex: 0 0 auto;
    font-size: 0.78rem;
    font-weight: 600;
    color: #8aa0aa;
    font-variant-numeric: tabular-nums;
}

/* ----- Steps ----- */
.step {
    display: flex;
    gap: 14px;
    padding: 16px;
    background: var(--duallo-card-gradient);
    border: 1px solid rgba(255, 255, 255, 0.05);
    border-radius: 14px;
}

.step.is-done {
    border-color: rgba(100, 255, 218, 0.28);
}

.step-badge {
    flex: 0 0 34px;
    width: 34px;
    height: 34px;
    display: grid;
    place-items: center;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    color: #8aa0aa;
}

.step.is-done .step-badge {
    background: rgba(100, 255, 218, 0.16);
    border-color: rgba(100, 255, 218, 0.45);
    color: var(--duallo-accent);
}

.step-body {
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.step-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
}

.step-title {
    font-size: 1.02rem;
    font-weight: 700;
    color: #fff;
    margin: 0;
}

.step-sub {
    font-size: 0.82rem;
    color: #8aa0aa;
    margin: 3px 0 0;
    line-height: 1.4;
}

.step-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
}

/* ----- Status chip ----- */
.status-chip {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 4px 10px;
    border-radius: 999px;
    font-size: 0.74rem;
    font-weight: 700;
    white-space: nowrap;
}

.status-chip.is-ok {
    color: var(--duallo-accent);
    background: rgba(100, 255, 218, 0.12);
    border: 1px solid rgba(100, 255, 218, 0.3);
}

.status-chip.is-pending {
    color: #ffb454;
    background: rgba(255, 180, 84, 0.12);
    border: 1px solid rgba(255, 180, 84, 0.3);
}

.status-chip.is-missing {
    color: #90a4ae;
    background: rgba(144, 164, 174, 0.12);
    border: 1px solid rgba(144, 164, 174, 0.3);
}

/* ----- Upload items ----- */
.upload-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.upload-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
    padding: 10px 12px;
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.02);
    border: 1px solid rgba(255, 255, 255, 0.05);
}

.upload-item.is-done {
    background: rgba(100, 255, 218, 0.05);
    border-color: rgba(100, 255, 218, 0.18);
}

.upload-info {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
}

.tick {
    color: #5a6970;
    flex: 0 0 auto;
}

.upload-item.is-done .tick {
    color: var(--duallo-accent);
}

.upload-text {
    display: flex;
    flex-direction: column;
    min-width: 0;
}

.upload-name {
    font-size: 0.88rem;
    font-weight: 600;
    color: #e3eef0;
}

.upload-meta {
    font-size: 0.74rem;
    color: #8aa0aa;
}

.upload-controls {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}

.upload-go {
    flex: 0 0 auto;
}

/* ----- Readiness checklist ----- */
.checklist {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    gap: 8px;
}

.check {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 10px;
    border-radius: 9px;
    background: rgba(255, 255, 255, 0.02);
    border: 1px solid rgba(255, 255, 255, 0.05);
    color: #8aa0aa;
}

.check.ok {
    color: #cfeee7;
    background: rgba(100, 255, 218, 0.06);
    border-color: rgba(100, 255, 218, 0.2);
}

.check .v-icon {
    color: #5a6970;
    flex: 0 0 auto;
}

.check.ok .v-icon {
    color: var(--duallo-accent);
}

.check-label {
    flex: 1 1 auto;
    font-size: 0.8rem;
    font-weight: 600;
    min-width: 0;
}

.check-count {
    flex: 0 0 auto;
    font-size: 0.74rem;
    font-weight: 700;
    color: var(--duallo-accent);
    font-variant-numeric: tabular-nums;
}

/* ----- Process actions ----- */
.process-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
}

/* ----- Danger zone ----- */
.danger-zone {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    margin-top: 6px;
    padding: 16px;
    border-radius: 14px;
    background: rgba(239, 83, 80, 0.04);
    border: 1px solid rgba(239, 83, 80, 0.22);
}

.danger-head {
    display: flex;
    align-items: center;
    gap: 8px;
}

.danger-title {
    font-size: 0.95rem;
    font-weight: 700;
    color: #ef9a9a;
    margin: 0;
}

.danger-hint {
    font-size: 0.82rem;
    color: #90a4ae;
    margin: 0;
    max-width: 620px;
    line-height: 1.45;
}

.cancel-card {
    background: var(--duallo-card-end);
    color: #fff;
}

.cancel-title {
    font-weight: 600;
}

.cancel-text {
    font-size: 0.9rem;
    color: #b0bec5;
    margin-bottom: 18px;
    line-height: 1.5;
}

.cancel-actions {
    justify-content: flex-end;
    gap: 8px;
    padding: 0 16px 16px;
}
</style>
